import DeleteIcon from '@mui/icons-material/Delete';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import PersonIcon from '@mui/icons-material/Person';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

const API_BASE = '';

const todayISO = () => new Date().toISOString().slice(0, 10);

const formatDate = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric', weekday: 'short' });
    } catch {
        return iso;
    }
};

function AdminShiftsPanel({ user, token }) {
    const [date, setDate] = useState(todayISO());
    const [shifts, setShifts] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedEmp, setSelectedEmp] = useState(null);
    const [notes, setNotes] = useState('');
    const [souvenirsAmount, setSouvenirsAmount] = useState('');
    const [souvenirsNotes, setSouvenirsNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);

    const authHeaders = useMemo(() => ({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    }), [token]);

    const canEdit = ['owner', 'admin', 'manager'].includes(user?.role);

    const showMessage = (severity, text) => {
        setMessage({ severity, text });
        setTimeout(() => setMessage(null), 4000);
    };

    const loadShifts = useCallback(async () => {
        if (!date) return;
        setLoading(true);
        try {
            const r = await fetch(`${API_BASE}/api/admin-shifts?date=${date}`, { headers: authHeaders });
            const data = await r.json();
            if (data.success) {
                setShifts(data.data || []);
            } else {
                showMessage('error', data.error || 'Ошибка загрузки смен');
            }
        } catch (e) {
            showMessage('error', e.message);
        } finally {
            setLoading(false);
        }
    }, [date, authHeaders]);

    const loadEmployees = useCallback(async () => {
        try {
            const r = await fetch(`${API_BASE}/api/employees`, { headers: authHeaders });
            const data = await r.json();
            const list = Array.isArray(data) ? data : (data.data || data.employees || []);
            // Только админы / менеджеры / владельцы — кто может выходить как «админ на смене»
            const filtered = list.filter(e => {
                const pos = (e.position || '').toLowerCase();
                return pos.includes('админ') || pos.includes('менедж') || pos.includes('владел') || !e.position;
            });
            setEmployees(filtered.length ? filtered : list);
        } catch (e) {
            showMessage('error', `Не удалось загрузить сотрудников: ${e.message}`);
        }
    }, [authHeaders]);

    useEffect(() => { loadEmployees(); }, [loadEmployees]);
    useEffect(() => { loadShifts(); }, [loadShifts]);

    const addShift = async () => {
        if (!selectedEmp || !date) {
            showMessage('warning', 'Выберите дату и сотрудника');
            return;
        }
        setBusy(true);
        try {
            const r = await fetch(`${API_BASE}/api/admin-shifts`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ shift_date: date, employee_id: selectedEmp.id, notes: notes || null }),
            });
            const data = await r.json();
            if (data.success) {
                setSelectedEmp(null);
                setNotes('');
                showMessage('success', 'Сотрудник добавлен на смену');
                loadShifts();
            } else {
                showMessage('error', data.error || 'Ошибка');
            }
        } catch (e) {
            showMessage('error', e.message);
        } finally {
            setBusy(false);
        }
    };

    const removeShift = async (id) => {
        setBusy(true);
        try {
            const r = await fetch(`${API_BASE}/api/admin-shifts/${id}`, {
                method: 'DELETE',
                headers: authHeaders,
            });
            const data = await r.json();
            if (data.success) {
                showMessage('success', 'Удалено');
                loadShifts();
            } else {
                showMessage('error', data.error || 'Ошибка');
            }
        } catch (e) {
            showMessage('error', e.message);
        } finally {
            setBusy(false);
        }
    };

    const distribute = async () => {
        const amount = parseFloat(souvenirsAmount);
        if (!amount || amount <= 0) {
            showMessage('warning', 'Введите сумму сувенирки');
            return;
        }
        if (shifts.length === 0) {
            showMessage('warning', 'Сначала добавьте хотя бы одного админа на смену');
            return;
        }
        setBusy(true);
        try {
            const r = await fetch(`${API_BASE}/api/admin-shifts/distribute-souvenirs`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ date, total_amount: amount, notes: souvenirsNotes || null }),
            });
            const data = await r.json();
            if (data.success) {
                showMessage('success', `Распределено ${data.share} ₽ на ${data.count} админ(а/ов)`);
                setSouvenirsAmount('');
                setSouvenirsNotes('');
            } else {
                showMessage('error', data.error || 'Ошибка');
            }
        } catch (e) {
            showMessage('error', e.message);
        } finally {
            setBusy(false);
        }
    };

    const availableEmployees = employees.filter(e => !shifts.some(s => s.employee_id === e.id));

    return (
        <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                Смены админов
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Отметьте, кто из админов работает в выбранную дату. На основании этого распределяется выручка от сувенирки.
            </Typography>

            {message && (
                <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
                    {message.text}
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Stack spacing={2}>
                                <TextField
                                    type="date"
                                    label="Дата смены"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {formatDate(date)}
                                </Typography>

                                {canEdit && (
                                    <>
                                        <Divider />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            Добавить админа на смену
                                        </Typography>
                                        <Autocomplete
                                            options={availableEmployees}
                                            getOptionLabel={(o) => `${o.full_name || o.name || ''}${o.position ? ` · ${o.position}` : ''}`}
                                            value={selectedEmp}
                                            onChange={(_, v) => setSelectedEmp(v)}
                                            renderInput={(params) => <TextField {...params} label="Сотрудник" />}
                                            isOptionEqualToValue={(o, v) => o.id === v.id}
                                        />
                                        <TextField
                                            label="Заметка (необязательно)"
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            fullWidth
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={<GroupAddIcon />}
                                            onClick={addShift}
                                            disabled={busy || !selectedEmp}
                                        >
                                            Добавить на смену
                                        </Button>
                                    </>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={7}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <PersonIcon color="primary" />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    На смене {formatDate(date)}
                                </Typography>
                                <Chip size="small" label={shifts.length} color="primary" />
                            </Stack>
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : shifts.length === 0 ? (
                                <Alert severity="info">На эту дату админы не отмечены</Alert>
                            ) : (
                                <List dense>
                                    {shifts.map((s) => (
                                        <ListItem key={s.id} divider>
                                            <ListItemText
                                                primary={s.employee_name}
                                                secondary={[s.employee_position, s.notes].filter(Boolean).join(' · ')}
                                            />
                                            {canEdit && (
                                                <ListItemSecondaryAction>
                                                    <IconButton
                                                        edge="end"
                                                        size="small"
                                                        color="error"
                                                        onClick={() => removeShift(s.id)}
                                                        disabled={busy}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            )}
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {canEdit && (
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <LocalAtmIcon color="warning" />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Распределить сувенирку за день
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Сумма будет поровну записана каждому админу на смене и попадёт в его расчёт зарплаты.
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            type="number"
                                            label="Сумма сувенирки, ₽"
                                            value={souvenirsAmount}
                                            onChange={(e) => setSouvenirsAmount(e.target.value)}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <TextField
                                            label="Заметка (необязательно)"
                                            value={souvenirsNotes}
                                            onChange={(e) => setSouvenirsNotes(e.target.value)}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Button
                                            variant="contained"
                                            color="warning"
                                            startIcon={<LocalAtmIcon />}
                                            onClick={distribute}
                                            disabled={busy || shifts.length === 0 || !souvenirsAmount}
                                            fullWidth
                                            sx={{ height: '100%' }}
                                        >
                                            Распределить
                                        </Button>
                                    </Grid>
                                </Grid>
                                {shifts.length > 0 && souvenirsAmount > 0 && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        Каждому админу: <b>{(parseFloat(souvenirsAmount) / shifts.length).toFixed(2)} ₽</b>
                                    </Alert>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}

export default AdminShiftsPanel;
